import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar, MapPin, Clock, Users, Share2, ThumbsUp, ThumbsDown, 
  MessageCircle, Send, ArrowLeft, Plus, Check, Copy, Wifi, WifiOff
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuth } from '@/contexts/AuthContext';
import ConsumerLayout from '@/components/ConsumerLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useItinerarySocket } from '@/hooks/useItinerarySocket';

const timeSlots = [
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM', '9:00 PM', '9:30 PM', '10:00 PM', '10:30 PM'
];

const ItemCard = ({ item, onVote, onComment, currentUserId, isAuthenticated }) => {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  
  const upVotes = item.votes_up?.length || 0;
  const downVotes = item.votes_down?.length || 0;
  const hasVotedUp = item.votes_up?.includes(currentUserId);
  const hasVotedDown = item.votes_down?.includes(currentUserId);

  const getItemLink = () => {
    switch (item.item_type) {
      case 'event': return `/events/${item.item_id}`;
      case 'venue': return `/venues/${item.item_id}`;
      case 'experience': return `/experiences/${item.item_id}`;
      default: return '#';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <Card className="border-l-4 border-l-indigo-600 hover:shadow-lg transition-shadow">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <Link to={getItemLink()} className="flex items-start gap-4 flex-1 group">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 flex flex-col items-center justify-center text-white">
                <span className="text-lg font-bold">{item.time_slot?.split(':')[0]}</span>
                <span className="text-xs">{item.time_slot?.includes('PM') ? 'PM' : 'AM'}</span>
              </div>
              <div>
                <h4 className="font-bold text-foreground group-hover:text-indigo-600 transition-colors">
                  {item.item_title}
                </h4>
                <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                  <MapPin className="w-4 h-4" />
                  {item.venue_name}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Badge variant="secondary" className="capitalize">
                    {item.item_type}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    Added by {item.added_by_name}
                  </span>
                </div>
              </div>
            </Link>
            
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-1 border rounded-full px-3 py-1.5 bg-white dark:bg-gray-900">
                <button
                  onClick={() => isAuthenticated ? onVote(item.id, 'up') : toast.error('Sign in to vote')}
                  className={cn(
                    "p-1 rounded-full transition-colors",
                    hasVotedUp ? "text-green-600 bg-green-100" : "text-muted-foreground hover:text-green-600"
                  )}
                >
                  <ThumbsUp className="w-4 h-4" />
                </button>
                <span className="text-sm font-bold min-w-[24px] text-center">{upVotes - downVotes}</span>
                <button
                  onClick={() => isAuthenticated ? onVote(item.id, 'down') : toast.error('Sign in to vote')}
                  className={cn(
                    "p-1 rounded-full transition-colors",
                    hasVotedDown ? "text-red-600 bg-red-100" : "text-muted-foreground hover:text-red-600"
                  )}
                >
                  <ThumbsDown className="w-4 h-4" />
                </button>
              </div>
              
              <button
                onClick={() => setShowComments(!showComments)}
                className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <MessageCircle className="w-4 h-4" />
                {item.comments?.length || 0}
              </button>
            </div>
          </div>
          
          {showComments && (
            <div className="mt-4 pt-4 border-t">
              <div className="space-y-2 mb-3">
                {item.comments?.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-2">No comments yet</p>
                ) : (
                  item.comments?.map((comment) => (
                    <div key={comment.id} className="bg-muted p-3 rounded-lg">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.timestamp).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-sm">{comment.text}</p>
                    </div>
                  ))
                )}
              </div>
              {isAuthenticated ? (
                <div className="flex gap-2">
                  <Input
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newComment) {
                        onComment(item.id, newComment);
                        setNewComment('');
                      }
                    }}
                  />
                  <Button
                    size="icon"
                    onClick={() => {
                      if (newComment) {
                        onComment(item.id, newComment);
                        setNewComment('');
                      }
                    }}
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-center text-muted-foreground">
                  <Link to="/auth" className="text-indigo-600 hover:underline">Sign in</Link> to comment
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ItineraryView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [copied, setCopied] = useState(false);

  // Real-time collaboration hook
  const {
    isConnected,
    presence,
    lastUpdate,
    emitItemAdded,
    emitItemRemoved,
    emitItemVoted,
    emitItemCommented,
    emitStatusChanged
  } = useItinerarySocket(itinerary?.id, user);

  useEffect(() => {
    fetchItinerary();
  }, [id]);

  // Handle real-time updates
  useEffect(() => {
    if (!lastUpdate) return;

    switch (lastUpdate.type) {
      case 'item_added':
        toast.info(`${lastUpdate.added_by} added a new stop`);
        fetchItinerary(); // Refresh to get new item
        break;
      case 'item_removed':
        toast.info(`${lastUpdate.removed_by} removed a stop`);
        fetchItinerary();
        break;
      case 'item_voted':
        toast.info(`${lastUpdate.voted_by} voted on an item`);
        fetchItinerary();
        break;
      case 'item_commented':
        toast.info(`${lastUpdate.commented_by} added a comment`);
        fetchItinerary();
        break;
      case 'status_changed':
        toast.info(`${lastUpdate.changed_by} changed status to ${lastUpdate.status}`);
        fetchItinerary();
        break;
      case 'user_joined':
        toast.info(`${lastUpdate.user_name} joined the itinerary`);
        break;
      case 'user_left':
        toast.info(`${lastUpdate.user_name} left the itinerary`);
        break;
      default:
        break;
    }
  }, [lastUpdate]);

  const fetchItinerary = async () => {
    try {
      const response = await api.get(`/itineraries/${id}`);
      setItinerary(response.data);
    } catch (error) {
      toast.error('Itinerary not found');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const voteItem = async (itemId, voteType) => {
    if (!itinerary) return;
    try {
      await api.post(`/itineraries/${itinerary.id}/items/${itemId}/vote`, null, {
        params: { vote_type: voteType }
      });
      emitItemVoted(itemId, voteType); // Broadcast to collaborators
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const commentItem = async (itemId, comment) => {
    if (!itinerary) return;
    try {
      await api.post(`/itineraries/${itinerary.id}/items/${itemId}/comment`, null, {
        params: { comment }
      });
      emitItemCommented(itemId, comment); // Broadcast to collaborators
      fetchItinerary();
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <ConsumerLayout hideFooter>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600" />
        </div>
      </ConsumerLayout>
    );
  }

  if (!itinerary) return null;

  const sortedItems = itinerary.items?.sort((a, b) => {
    const timeA = timeSlots.indexOf(a.time_slot);
    const timeB = timeSlots.indexOf(b.time_slot);
    return timeA - timeB;
  }) || [];

  const isCollaborator = itinerary.collaborators?.some(c => c.user_id === user?.id);

  // Generate avatar colors based on user name
  const getAvatarColor = (name) => {
    const colors = ['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500', 'bg-purple-500', 'bg-pink-500', 'bg-indigo-500'];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  return (
    <ConsumerLayout hideFooter>

      {/* Hero Header */}
      <section className="relative py-12 bg-gradient-to-br from-indigo-600 to-purple-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            className="mb-4 text-white/80 hover:text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Badge className="bg-white/20 text-white border-0">
                  <Users className="w-3 h-3 mr-1" />
                  {itinerary.collaborators?.length || 1} collaborators
                </Badge>
                
                {/* Real-time connection status */}
                <Badge 
                  className={cn(
                    "border-0",
                    isConnected 
                      ? "bg-green-500/20 text-green-200" 
                      : "bg-red-500/20 text-red-200"
                  )}
                >
                  {isConnected ? (
                    <><Wifi className="w-3 h-3 mr-1" /> Live</>
                  ) : (
                    <><WifiOff className="w-3 h-3 mr-1" /> Offline</>
                  )}
                </Badge>
              </div>
              
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">
                {itinerary.title}
              </h1>
              <div className="flex items-center gap-4 text-white/80">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  {format(new Date(itinerary.date), 'EEEE, MMMM d, yyyy')}
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  {sortedItems.length} stops planned
                </div>
              </div>
              <p className="text-white/70 mt-2">
                Created by {itinerary.owner_name}
              </p>
              
              {/* Online presence indicators */}
              {presence.length > 0 && (
                <div className="flex items-center gap-2 mt-4">
                  <span className="text-white/60 text-sm">Viewing now:</span>
                  <TooltipProvider>
                    <div className="flex -space-x-2">
                      <AnimatePresence>
                        {presence.slice(0, 5).map((p, i) => (
                          <Tooltip key={p.user_id || i}>
                            <TooltipTrigger asChild>
                              <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0, opacity: 0 }}
                              >
                                <Avatar className={cn("w-8 h-8 border-2 border-white", getAvatarColor(p.user_name))}>
                                  <AvatarFallback className="text-white text-xs font-bold bg-transparent">
                                    {p.user_name?.charAt(0)?.toUpperCase() || '?'}
                                  </AvatarFallback>
                                </Avatar>
                              </motion.div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{p.user_name}</p>
                            </TooltipContent>
                          </Tooltip>
                        ))}
                      </AnimatePresence>
                      {presence.length > 5 && (
                        <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white flex items-center justify-center">
                          <span className="text-white text-xs font-bold">+{presence.length - 5}</span>
                        </div>
                      )}
                    </div>
                  </TooltipProvider>
                </div>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="secondary"
                onClick={copyLink}
              >
                {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
                {copied ? 'Copied!' : 'Copy Link'}
              </Button>
              {isCollaborator && (
                <Button 
                  className="bg-white text-indigo-600 hover:bg-white/90"
                  onClick={() => navigate(`/itinerary/new?edit=${itinerary.id}`)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Stop
                </Button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Timeline */}
          <div className="lg:col-span-2 space-y-4">
            <h2 className="text-xl font-bold text-foreground mb-4">The Plan</h2>
            
            {sortedItems.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-bold mb-2">No stops added yet</h3>
                  <p className="text-muted-foreground">
                    This itinerary is still being planned
                  </p>
                </CardContent>
              </Card>
            ) : (
              sortedItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  onVote={voteItem}
                  onComment={commentItem}
                  currentUserId={user?.id}
                  isAuthenticated={isAuthenticated}
                />
              ))
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Collaborators */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Who's Planning
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {itinerary.collaborators?.map((collab, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-bold">
                        {collab.name?.charAt(0) || 'U'}
                      </div>
                      <div>
                        <p className="font-medium">{collab.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">{collab.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Join CTA */}
            {!isCollaborator && isAuthenticated && (
              <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50">
                <CardContent className="p-5 text-center">
                  <h4 className="font-bold mb-2">Want to join the fun?</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Start suggesting places and vote on activities
                  </p>
                  <Button className="bg-indigo-600 hover:bg-indigo-700">
                    Request to Join
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Create Your Own */}
            <Card>
              <CardContent className="p-5 text-center">
                <h4 className="font-bold mb-2">Planning your own night?</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Create a collaborative itinerary and invite your friends
                </p>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/itinerary/new')}
                >
                  Create Itinerary
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </ConsumerLayout>
  );
};

export default ItineraryView;
